import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { FiArrowLeft, FiEdit2, FiTrash2, FiCalendar, FiMapPin, FiUsers, FiDollarSign, FiClock, FiUser, FiFileText, FiAward } from 'react-icons/fi'
import Breadcrumb from "../../utils/breadcrumbs"
import formatDate from "../../utils/format-date"
import ConfirmationModal from "../../ui/confirmation-modal"
import toast from "react-hot-toast"
import api from '../../libs/axios'
import Loader from '../../utils/loader'

interface ITournamentDetails {
  id: number
  name: string
  description?: string
  organizer_id: number
  tournament_category: string
  status: string
  start_date: string
  end_date?: string
  registration_deadline?: string
  max_teams?: number
  entry_fee?: string
  prize_pool?: string
  location?: string
  banner_url?: string
  tournament_logo_url?: string
  last_batter_rule: boolean
  pitch_type: string
  ball_type: string
  ground_id: string
  more_teams: boolean
  match_officials: boolean
  match_type?: string
  rules?: string
  created_at?: string
  updated_at?: string
  organizer: {
    id: number
    phone_number: string
    role_id: number
    details: {
      first_name: string
      last_name: string
    }
  }
  ground: {
    id: number
    name: string
    value: string
    description: string
  }
}

const ViewTournament = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const tournamentId = location.state?.tournamentId

  const [tournament, setTournament] = useState<ITournamentDetails | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  const fetchTournament = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/tournaments/${tournamentId}`)
      setTournament(response.data.tournament)
    } catch (error) {
      console.error(error)
      toast.error("Failed to fetch tournament details")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (tournamentId) {
      fetchTournament()
    }
  }, [tournamentId])

  // Get status style
  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case "ongoing":
        return "bg-red-100 text-red-800 border-red-200"
      case "completed":
        return "bg-green-100 text-green-800 border-green-200"
      case "registration_open":
        return "bg-blue-100 text-blue-800 border-blue-200"
      case "registration_closed":
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case "draft":
        return "bg-gray-100 text-gray-800 border-gray-200"
      case "cancelled":
        return "bg-red-100 text-red-600 border-red-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  // Handle delete
  const handleDeleteConfirm = async () => {
    if (tournament) {
      try {
        await api.delete(`/tournaments/${tournament.id}`)
        toast.success(`Tournament deleted successfully!`)
        navigate('/tournaments')
      } catch (err) {
        toast.error("Error deleting tournament")
      }
      setDeleteConfirmOpen(false)
    }
  }

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6">
        <Loader />
      </div>
    )
  }

  if (!tournament) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-500">Tournament not found</div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/tournaments"
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 shadow-sm"
          >
            <FiArrowLeft className="w-4 h-4" />
            <span className="font-medium">Back</span>
          </Link>
          <h2 className="text-xl font-bold text-primary-950 flex items-center">
            <span className="bg-primary-700 w-1 h-6 mr-3 rounded-full"></span>
            Tournament Details
          </h2>
        </div>
        <Breadcrumb />
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Action Header */}
        <div className="flex justify-end items-center p-6 border-b bg-gradient-to-r from-gray-50 to-white">
          <div className="flex gap-3">
            <Link
              to="/tournaments/edit"
              state={{ tournamentId: tournament.id }}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              <FiEdit2 className="w-4 h-4" />
              Edit
            </Link>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              <FiTrash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>

        {/* Tournament Header */}
        <div className="p-6 border-b bg-gradient-to-br from-white to-gray-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-3 leading-tight">{tournament.name}</h1>
              {tournament.description && (
                <p className="text-gray-600 mb-4 text-lg leading-relaxed">{tournament.description}</p>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`px-4 py-2 text-xs rounded-full font-medium capitalize border ${getStatusStyle(tournament.status)} shadow-sm`}>
                  {tournament.status.replace(/_/g, " ")}
                </span>
                <span className="px-4 py-2 text-xs bg-gray-100 text-gray-800 rounded-full font-medium capitalize border border-gray-200 shadow-sm">
                  {tournament.tournament_category}
                </span>
              </div>
            </div>
            {tournament.tournament_logo_url && (
              <div className="ml-6">
                <img
                  src={tournament.tournament_logo_url}
                  alt="Tournament Logo"
                  className="w-28 h-28 object-cover rounded-xl border-2 border-gray-200 shadow-md"
                />
              </div>
            )}
          </div>
        </div>

        {/* Tournament Banner */}
        {tournament.banner_url && (
          <div className="p-6 border-b">
            <img
              src={tournament.banner_url}
              alt="Tournament Banner"
              className="w-full h-56 object-cover rounded-xl border border-gray-200 shadow-sm"
            />
          </div>
        )}

        {/* Tournament Details Grid - 3 Cards in One Line */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Date & Time and Location Combined */}
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                <FiCalendar className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Date & Location</h3>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Start:</span>
                  <span className="text-gray-900 font-medium">{formatDate(tournament.start_date)}</span>
                </div>
                {tournament.end_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">End:</span>
                    <span className="text-gray-900 font-medium">{formatDate(tournament.end_date)}</span>
                  </div>
                )}
                {tournament.registration_deadline && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Registration:</span>
                    <span className="text-gray-900 font-medium">{formatDate(tournament.registration_deadline)}</span>
                  </div>
                )}
                <div className="pt-5 mt-3 border-t">
                  <div className="flex items-center gap-2 mb-2">
                    <FiMapPin className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-gray-900">{tournament.location || "Location TBD"}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Ground:</span> {tournament.ground.name}
                  </div>
                  {/* <div className="text-xs text-gray-500 mt-1 italic">{tournament.ground.description}</div> */}
                </div>
              </div>
            </div>

            {/* Game Format */}
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                <FiClock className="w-5 h-5 text-orange-600" />
                <h3 className="font-semibold text-gray-900">Game Format</h3>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Pitch Type:</span>
                  <span className="text-gray-900 font-medium capitalize">{tournament.pitch_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Ball Type:</span>
                  <span className="text-gray-900 font-medium capitalize">{tournament.ball_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Batter:</span>
                  <span className={`font-medium ${tournament.last_batter_rule ? 'text-green-600' : 'text-red-600'}`}>
                    {tournament.last_batter_rule ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Officials:</span>
                  <span className={`font-medium ${tournament.match_officials ? 'text-green-600' : 'text-red-600'}`}>
                    {tournament.match_officials ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">More Teams:</span>
                  <span className={`font-medium ${tournament.more_teams ? 'text-green-600' : 'text-red-600'}`}>
                    {tournament.more_teams ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </div>

            {/* Organizer */}
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                <FiUser className="w-5 h-5 text-teal-600" />
                <h3 className="font-semibold text-gray-900">Organizer</h3>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <div className="font-medium inline text-gray-900 text-base">
                    {tournament.organizer.details.first_name} {tournament.organizer.details.last_name}
                  </div>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Phone No.:</span>
                  <span className="text-gray-900 font-medium">{tournament.organizer.phone_number}</span>
                </div>
              </div>
            </div>

            {/* Teams & Capacity */}
            {/* <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-5 rounded-xl border border-purple-100 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FiUsers className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 text-lg">Teams</h3>
              </div>
            </div> */}

            {/* Financial Info */}
            {/* <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-5 rounded-xl border border-yellow-100 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FiDollarSign className="w-5 h-5 text-yellow-600" />
                </div>
                <h3 className="font-semibold text-gray-900 text-lg">Financial Details</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Entry Fee:</span>
                  <span className="text-gray-900 font-semibold">
                    {tournament.entry_fee && parseFloat(tournament.entry_fee) > 0
                      ? `₹${parseFloat(tournament.entry_fee).toLocaleString()}`
                      : "Free"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Prize Pool:</span>
                  <span className="text-gray-900 font-semibold">
                    {tournament.prize_pool && parseFloat(tournament.prize_pool) > 0
                      ? `₹${parseFloat(tournament.prize_pool).toLocaleString()}`
                      : "No prize"}
                  </span>
                </div>
              </div>
            </div> */}

          </div>

          {/* Rules Section */}
          {tournament.rules && (
            <div className="mt-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                  <FiFileText className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-900">Tournament Rules</h3>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {tournament.rules}
                </div>
              </div>
            </div>
          )}

          {/* Meta Information */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
              <div>
                <span className="font-medium">Created:</span> {formatDate(tournament.created_at as string)}
              </div>
              <div>
                <span className="font-medium">Last Updated:</span> {formatDate(tournament.updated_at as string)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <ConfirmationModal
          isOpen={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Tournament"
          description={`Are you sure you want to delete "${tournament.name}"? This action cannot be undone.`}
          loading={false}
        />
      )}
    </div>
  )
}

export default ViewTournament